// src/tools/skills.ts

import type { Dirent } from "node:fs";
import fs from "node:fs/promises";
import path from "node:path";
import {
  buildRequiresPreamble,
  parseSkillFrontmatter,
  parseSkillRequirements,
  validateRequirements,
} from "@skaile/workspaces/resolver";
import type { ToolResult } from "../types.js";
import { err, ok, okJson } from "../types.js";
import { findAiAssetsRoot } from "../utils/ai-assets.js";

interface SkillEntry {
  id: string;
  name: string;
  description: string;
  source?: string;
  version?: string;
  keywords?: string[];
  path: string;
  domain: string;
}

async function walkForSkillMd(dir: string, domain: string, acc: SkillEntry[]): Promise<void> {
  let entries: Dirent<string>[];
  try {
    entries = await fs.readdir(dir, { withFileTypes: true });
  } catch {
    return;
  }

  // Check if this directory contains a SKILL.md
  const hasSkillMd = entries.some((e) => e.isFile() && e.name === "SKILL.md");
  if (hasSkillMd) {
    const skillMdPath = path.join(dir, "SKILL.md");
    try {
      const content = await fs.readFile(skillMdPath, "utf-8");
      const fm = parseSkillFrontmatter(content);
      const name = typeof fm.name === "string" ? fm.name : path.basename(dir);
      const description = typeof fm.description === "string" ? fm.description : "";
      const source = typeof fm.source === "string" ? fm.source : undefined;
      const version = typeof fm.version === "string" ? fm.version : undefined;
      const keywords = Array.isArray(fm.keywords) ? (fm.keywords as string[]) : undefined;
      acc.push({
        id: path.basename(dir),
        name,
        description,
        source,
        version,
        keywords,
        path: skillMdPath,
        domain,
      });
    } catch {
      // skip unreadable files
    }
    return; // don't recurse inside a skill directory
  }

  // Recurse into subdirectories
  for (const entry of entries) {
    if (entry.isDirectory()) {
      await walkForSkillMd(path.join(dir, entry.name), domain, acc);
    }
  }
}

async function scanSkills(aiAssetsRoot: string, domainFilter?: string): Promise<SkillEntry[]> {
  let domains: string[];
  try {
    const entries = await fs.readdir(aiAssetsRoot, { withFileTypes: true });
    domains = entries
      .filter((e) => e.isDirectory())
      .map((e) => e.name)
      .filter((name) => !domainFilter || name === domainFilter);
  } catch {
    return [];
  }

  const acc: SkillEntry[] = [];
  for (const domain of domains) {
    const skillsDir = path.join(aiAssetsRoot, domain, "skills");
    try {
      await fs.access(skillsDir);
    } catch {
      continue; // no skills/ dir in this domain
    }
    await walkForSkillMd(skillsDir, domain, acc);
  }
  return acc;
}

// ── Exported tool functions ────────────────────────────────────────────────────

/**
 * List all skills in the `ai-assets` catalog, optionally filtered by domain.
 *
 * Walks every `<domain>/skills/` directory under the `ai-assets` root looking
 * for `SKILL.md` files.  Returns a JSON array of skill summaries
 * `{ id, name, description, source, version, keywords, domain }`.
 *
 * @param projectDir - Absolute path to the skaile project root.
 * @param domain     - Optional domain name filter (e.g. `"skaile-development"`).
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function skillList(
  projectDir: string,
  domain: string | undefined,
): Promise<ToolResult> {
  try {
    const aiAssetsRoot = await findAiAssetsRoot(projectDir);
    if (!aiAssetsRoot) return okJson([]);
    const skills = await scanSkills(aiAssetsRoot, domain);
    return okJson(
      skills.map((s) => ({
        id: s.id,
        name: s.name,
        description: s.description,
        source: s.source,
        version: s.version,
        keywords: s.keywords,
        domain: s.domain,
      })),
    );
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * List all skill domains — the top-level directories under `ai-assets` that
 * contain a `skills/` subdirectory.
 *
 * Returns a JSON array of `{ name, path }` objects.
 *
 * @param projectDir - Absolute path to the skaile project root.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function skillListDomains(projectDir: string): Promise<ToolResult> {
  try {
    const aiAssetsRoot = await findAiAssetsRoot(projectDir);
    if (!aiAssetsRoot) return okJson([]);
    const entries = await fs.readdir(aiAssetsRoot, { withFileTypes: true });
    const domains = entries
      .filter((e) => e.isDirectory())
      .map((e) => ({ name: e.name, path: path.join(aiAssetsRoot, e.name) }));
    return okJson(domains);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Show the full `SKILL.md` content for a skill.
 *
 * @param projectDir - Absolute path to the skaile project root.
 * @param skillId    - Skill ID (directory name) as returned by {@link skillList}.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function skillShow(projectDir: string, skillId: string): Promise<ToolResult> {
  try {
    const aiAssetsRoot = await findAiAssetsRoot(projectDir);
    if (!aiAssetsRoot) return err(`Skill not found: ${skillId}`);
    const skills = await scanSkills(aiAssetsRoot, undefined);
    const skill = skills.find((s) => s.id === skillId);
    if (!skill) return err(`Skill not found: ${skillId}`);
    const content = await fs.readFile(skill.path, "utf-8");
    return ok(content);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Search skills by name, description, ID, or keywords.
 *
 * Case-insensitive substring match against all text fields in every skill
 * entry.  Returns the same summary shape as {@link skillList}.
 *
 * @param projectDir - Absolute path to the skaile project root.
 * @param query      - Search string.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function skillSearch(projectDir: string, query: string): Promise<ToolResult> {
  try {
    const aiAssetsRoot = await findAiAssetsRoot(projectDir);
    if (!aiAssetsRoot) return okJson([]);
    const skills = await scanSkills(aiAssetsRoot, undefined);
    const q = query.toLowerCase();
    const matched = skills.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.id.toLowerCase().includes(q) ||
        (s.keywords ?? []).some((k) => k.toLowerCase().includes(q)),
    );
    return okJson(
      matched.map((s) => ({
        id: s.id,
        name: s.name,
        description: s.description,
        source: s.source,
        version: s.version,
        keywords: s.keywords,
        domain: s.domain,
      })),
    );
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Validate that all prerequisites declared in a skill's `SKILL.md` are
 * satisfied in `targetDir`.
 *
 * Uses `@skaile/workspaces/resolver` to parse the `REQUIRES` / `READS` / `WRITES`
 * DSL and run the requirement checks against the filesystem.  Returns a
 * validation report as JSON.
 *
 * @param projectDir - Absolute path to the skaile project root (for skill lookup).
 * @param skillId    - Skill ID as returned by {@link skillList}.
 * @param targetDir  - Directory to validate against (typically the user's project).
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function skillCheckRequirements(
  projectDir: string,
  skillId: string,
  targetDir: string,
): Promise<ToolResult> {
  try {
    const aiAssetsRoot = await findAiAssetsRoot(projectDir);
    if (!aiAssetsRoot) return err(`Skill not found: ${skillId}`);
    const skills = await scanSkills(aiAssetsRoot, undefined);
    const skill = skills.find((s) => s.id === skillId);
    if (!skill) return err(`Skill not found: ${skillId}`);
    const content = await fs.readFile(skill.path, "utf-8");

    const requirements = parseSkillRequirements(content);
    const report = await validateRequirements(skillId, requirements, targetDir);
    return okJson(report);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Get the formatted `REQUIRES`/`INPUT`/`READS`/`WRITES` DSL preamble for a skill.
 *
 * Uses `@skaile/workspaces/resolver` to build the requirements preamble that can be
 * prepended to the skill prompt for injection into the agent's context.
 * Returns `"(no requirements declared)"` when the skill has no requirements.
 *
 * @param projectDir - Absolute path to the skaile project root.
 * @param skillId    - Skill ID as returned by {@link skillList}.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function skillGetPreamble(projectDir: string, skillId: string): Promise<ToolResult> {
  try {
    const aiAssetsRoot = await findAiAssetsRoot(projectDir);
    if (!aiAssetsRoot) return err(`Skill not found: ${skillId}`);
    const skills = await scanSkills(aiAssetsRoot, undefined);
    const skill = skills.find((s) => s.id === skillId);
    if (!skill) return err(`Skill not found: ${skillId}`);
    const content = await fs.readFile(skill.path, "utf-8");

    const requirements = parseSkillRequirements(content);
    const preamble = buildRequiresPreamble(requirements, skillId);
    return ok(preamble || "(no requirements declared)");
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}
